export const DEFAULT_MINE_PROBABILITY = 0.15;

// Set of coordinates "x,y" guaranteed to be safe for first-click openings
const safeOpeningCells = new Set<string>();

/**
 * Registers a coordinate (x, y) and its 3x3 neighborhood as safe from mines.
 * Ensures the first tap in an area lands on a 0-mine cell and triggers a flood fill cascade.
 */
export function registerSafeOpening(x: number, y: number): void {
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      safeOpeningCells.add(`${x + dx},${y + dy}`);
    }
  }
}

/**
 * Clears all registered safe opening centers (e.g. on game reset).
 */
export function clearSafeOpenings(): void {
  safeOpeningCells.clear();
}

/**
 * Fast, high-quality 32-bit hash function that deterministically maps a game seed
 * and 2D world coordinates (x, y) to a normalized float in range [0, 1).
 */
export function hashCell(seedStr: string, x: number, y: number): number {
  let h = 0x811c9dc5; // FNV 32-bit offset basis

  for (let i = 0; i < seedStr.length; i++) {
    h ^= seedStr.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }

  h ^= x | 0;
  h = Math.imul(h, 0x01000193);

  h ^= y | 0;
  h = Math.imul(h, 0x01000193);

  // Final avalanche mixer
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;

  return (h >>> 0) / 4294967296;
}

/**
 * Returns whether a cell at (x, y) contains a mine for a given seed and probability.
 */
export function isMine(
  seed: string,
  x: number,
  y: number,
  mineProbability: number = DEFAULT_MINE_PROBABILITY
): boolean {
  if (safeOpeningCells.has(`${x},${y}`)) {
    return false;
  }
  return hashCell(seed, x, y) < mineProbability;
}

/**
 * Helper to count total mines in a 10x10 box (boxX, boxY).
 */
export function countBoxMines(
  seed: string,
  boxX: number,
  boxY: number,
  mineProbability: number = DEFAULT_MINE_PROBABILITY
): number {
  let count = 0;
  const startX = boxX * 10;
  const startY = boxY * 10;

  for (let cy = 0; cy < 10; cy++) {
    for (let cx = 0; cx < 10; cx++) {
      if (isMine(seed, startX + cx, startY + cy, mineProbability)) {
        count++;
      }
    }
  }

  return count;
}
